/**
 * Typing-animation text component for the onboarding flow.
 *
 * Reveals one character at a time with support for inline **bold** and
 * __underline__ emphasis. Uses direct DOM manipulation via refs so the
 * per-character update does not go through React's reconciler — that
 * was the source of the perceived lag at 55 ms cadence. React renders
 * the static segment skeleton exactly once; the interval only writes
 * textContent into pre-existing span nodes.
 *
 * The untyped tail is rendered invisible (not removed) so the layout
 * is stable from character zero and centered text does not shift as
 * it fills in.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";

interface Segment {
  text: string;
  bold?: boolean;
  underline?: boolean;
}

function parseSegments(input: string): Segment[] {
  const segments: Segment[] = [];
  const pattern = /\*\*([^*]+)\*\*|__([^_]+)__|([^*_]+)/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(input)) !== null) {
    if (match[1] !== undefined) segments.push({ text: match[1], bold: true });
    else if (match[2] !== undefined) segments.push({ text: match[2], underline: true });
    else if (match[3] !== undefined) segments.push({ text: match[3] });
  }
  return segments;
}

function segmentClass(seg: Segment): string | undefined {
  const parts: string[] = [];
  if (seg.bold) parts.push("font-bold text-foreground");
  if (seg.underline) parts.push("underline decoration-foreground underline-offset-4");
  return parts.length > 0 ? parts.join(" ") : undefined;
}

interface TypedTextProps {
  text: string;
  /** Milliseconds per character. Default 55 for a contemplative pace. */
  speed?: number;
  /** Fires once when every character is visible. */
  onComplete?: () => void;
  className?: string;
}

export function TypedText({
  text,
  speed = 55,
  onComplete,
  className,
}: TypedTextProps) {
  const segments = useMemo(() => parseSegments(text), [text]);
  const plainLength = useMemo(
    () => segments.reduce((n, s) => n + s.text.length, 0),
    [segments]
  );

  const visibleRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const hiddenRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  /**
   * Write the current state directly to DOM nodes. Called on every
   * interval tick and from skip(). No React state is touched.
   */
  const writeToDom = useCallback(
    (count: number) => {
      let consumed = 0;
      for (let i = 0; i < segments.length; i += 1) {
        const seg = segments[i];
        const visInThis = Math.max(0, Math.min(seg.text.length, count - consumed));
        const visibleEl = visibleRefs.current[i];
        const hiddenEl = hiddenRefs.current[i];
        if (visibleEl) visibleEl.textContent = seg.text.slice(0, visInThis);
        if (hiddenEl) hiddenEl.textContent = seg.text.slice(visInThis);
        consumed += seg.text.length;
      }
    },
    [segments]
  );

  useEffect(() => {
    completedRef.current = false;
    writeToDom(0);

    let count = 0;
    intervalRef.current = setInterval(() => {
      count += 1;
      writeToDom(count);
      if (count >= plainLength) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (!completedRef.current) {
          completedRef.current = true;
          onCompleteRef.current?.();
        }
      }
    }, speed);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [segments, plainLength, speed, writeToDom]);

  const skip = useCallback(() => {
    if (completedRef.current) return;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    writeToDom(plainLength);
    completedRef.current = true;
    onCompleteRef.current?.();
  }, [plainLength, writeToDom]);

  return (
    <span className={className} onClick={skip}>
      {segments.map((seg, i) => (
        <span key={i} className={segmentClass(seg)}>
          <span
            ref={(el) => {
              visibleRefs.current[i] = el;
            }}
          />
          <span
            className="invisible"
            ref={(el) => {
              hiddenRefs.current[i] = el;
            }}
          />
        </span>
      ))}
    </span>
  );
}
